import React, { useCallback, useContext, useState } from 'react';
import { CustomerServiceContext } from './CustomerServiceContext';

import { IArticleInfo, ICategorizedArticle } from './ICustomerService';
import Link from './Link';
import MinusIcon from './MinusIcon';
import PlusIcon from './PlusIcon';
import RightChevron from './RightChevron';
import useBreakpoint from '../utils/useBreakPoint';
import { useTranslation } from 'react-i18next';
import { en, fr } from './i18n';
import { Contact } from './ArticleSideNavigation';
import HelpChatIcon from './HelpChatIcon';

export interface CategorizedArticlesProps {
  articles: ICategorizedArticle[];
}

const CategorizedArticles = ({ articles }: CategorizedArticlesProps) => {
  const CATEGORIZED_ARTICLE_SEE_MORE_LIMIT = 4;

  const { i18n, t } = useTranslation('customer-service');
  i18n.addResourceBundle('en', 'customer-service', en, true, true);
  i18n.addResourceBundle('fr', 'customer-service', fr, true, true);

  const { setView, setUrlName } = useContext(CustomerServiceContext);

  const [isExpanded, setIsExpanded] = useState<boolean[]>(() => {
    return new Array(articles.length).fill(false);
  });

  const [mobileExpandedIndex, setMobileExpandedIndex] = useState(-1);

  const { isMobile } = useBreakpoint();

  const displayArticles = useCallback(
    (arr: IArticleInfo[], i: number) => {
      if (isExpanded[i]) {
        return arr;
      }

      const limit = Math.min(arr.length, CATEGORIZED_ARTICLE_SEE_MORE_LIMIT);
      return arr.slice(0, limit);
    },
    [articles, isExpanded],
  );

  const [contactSection] = useState<Array<Contact>>(() => {
    return [
      {
        urlName: 'contact-info',
        text: t('contactInfo'),
      },
      {
        urlName: 'email-inquiry',
        text: t('emailInquiry'),
      },
      {
        urlName: 'billing-refund-inquiry',
        text: t('billingRefundInquiry'),
      },
      {
        urlName: 'send-feedback',
        text: t('sendFeedback'),
      },
    ];
  });

  const isUnderLimit = useCallback(
    (arr: IArticleInfo[]) => {
      return arr.length <= CATEGORIZED_ARTICLE_SEE_MORE_LIMIT;
    },
    [displayArticles],
  );

  const buttonText = useCallback(
    (i: number) => {
      return isExpanded[i] ? t('seeLess') : t('seeMore');
    },
    [isExpanded],
  );

  const onExpandButtonClick = (i: number) => {
    setIsExpanded((prev) => {
      const arr: boolean[] = [];

      for (let j = 0; j < prev.length; j++) {
        arr.push(j === i ? !prev[j] : prev[j]);
      }

      return arr;
    });
  };

  const mobileCategoryTitleClick = (i: number) => {
    setMobileExpandedIndex((prev) => (prev === i ? -1 : i));
  };

  return (
    <>
      <h3 className="customer-service-faq__topics-title">{t('browseByHelpTopics')}</h3>
      {!isMobile && (
        <div className="customer-service-faq__topics-grid">
          {articles.map((categorizedArticle, categorizedArticleIndex) => (
            <div className="customer-service-faq__topics-grid__article" key={categorizedArticle.Category.Name}>
              <div className="customer-service-faq__topics-grid__article__title-icon">
                <img
                  className="customer-service-faq__topics-grid__article__title-icon__self"
                  src={categorizedArticle.Category.ImageUrl}
                  alt={`${categorizedArticle.Category.Name} icon`}
                />
              </div>
              <div className="customer-service-faq__topics-grid__article__wrapper">
                <div className="customer-service-faq__topics-grid__article__title">
                  <h4 className="customer-service-faq__topics-grid__article__title-text">
                    {categorizedArticle.Category.Name}
                  </h4>
                </div>
                <ul className="customer-service-faq__topics-grid__article__list">
                  {displayArticles(categorizedArticle.Articles, categorizedArticleIndex).map((article) => (
                    <li className="customer-service-faq__topics-grid__article__list-item" key={article.UrlName}>
                      <Link
                        className="customer-service-faq__topics-grid__article__list-item-link"
                        href={`/pages/customer-service/${article.UrlName}`}
                        dangerouslySetInnerHTML={{ __html: article.Title }}
                        onClick={(e) => {
                          e.preventDefault();
                          setView('article');
                          setUrlName(article.UrlName);
                        }}
                      />
                      <RightChevron className="customer-service-faq__topics-grid__article__list-item-icon" />
                    </li>
                  ))}
                </ul>
                {!isUnderLimit(categorizedArticle.Articles) && (
                  <button
                    onClick={() => onExpandButtonClick(categorizedArticleIndex)}
                    className="customer-service-faq__topics-grid__article__expand-button"
                  >
                    <span className="customer-service-faq__topics-grid__article__expand-button-text">
                      {buttonText(categorizedArticleIndex)}
                    </span>
                  </button>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      {isMobile && (
        <ul className="customer-service-faq__topics-accordion">
          {articles.map((categorizedArticle, categorizedArticleIndex) => (
            <li key={categorizedArticleIndex} className="customer-service-faq__topics-accordion__item">
              <button
                className="customer-service-faq__topics-accordion__item-title"
                onClick={() => mobileCategoryTitleClick(categorizedArticleIndex)}
              >
                <div className="customer-service-faq__topics-accordion__item-title__lhs">
                  <div className="customer-service-faq__topics-accordion__item-title__lhs__icon">
                    <img
                      className="customer-service-faq__topics-accordion__item-title__lhs__icon__self"
                      src={categorizedArticle.Category.ImageUrl}
                      alt={`${categorizedArticle.Category.Name} icon`}
                    />
                  </div>
                  <h4 className="customer-service-faq__topics-accordion__item-title__lhs__text">
                    {categorizedArticle.Category.Name}
                  </h4>
                </div>
                <div className="customer-service-faq__topics-accordion__item-title__rhs">
                  {categorizedArticleIndex === mobileExpandedIndex ? <MinusIcon /> : <PlusIcon />}
                </div>
              </button>
              {categorizedArticleIndex === mobileExpandedIndex && (
                <ul className="customer-service-faq__topics-accordion__item-articles">
                  {categorizedArticle.Articles.map((article) => (
                    <li key={article.UrlName} className="customer-service-faq__topics-accordion__item-articles__item">
                      <Link
                        href={`/pages/customer-service/${article.UrlName}`}
                        dangerouslySetInnerHTML={{ __html: article.Title }}
                        className="customer-service-faq__topics-accordion__item-articles__item-title"
                        onClick={() => {
                          setView('article');
                          setUrlName(article.UrlName);
                        }}
                      />
                    </li>
                  ))}
                </ul>
              )}
            </li>
          ))}
          <li key={articles.length} className="customer-service-faq__topics-accordion__item">
            <button
              className="customer-service-faq__topics-accordion__item-title"
              onClick={() => mobileCategoryTitleClick(articles.length)}
            >
              <div className="customer-service-faq__topics-accordion__item-title__lhs">
                <div className="customer-service-faq__topics-accordion__item-title__lhs__icon">
                  <HelpChatIcon className="customer-service-faq__article__side-nav__nav__item__title__lhs__icon--contact" />
                </div>
                <h4 className="customer-service-faq__topics-accordion__item-title__lhs__text">
                  {t('contactTsc')}
                </h4>
              </div>
              <div className="customer-service-faq__topics-accordion__item-title__rhs">
                {articles.length === mobileExpandedIndex ? <MinusIcon /> : <PlusIcon />}
              </div>
            </button>
            {articles.length === mobileExpandedIndex && (
              <ul className="customer-service-faq__topics-accordion__item-articles">
                {contactSection.map((contact) => (
                  <li key={contact.urlName} className="customer-service-faq__topics-accordion__item-articles__item">
                    <Link
                      href={`/pages/customer-service/${contact.urlName}`}
                      dangerouslySetInnerHTML={{ __html: contact.text }}
                      className="customer-service-faq__topics-accordion__item-articles__item-title"
                      onClick={() => {
                        setView('article');
                        setUrlName(contact.urlName);
                      }}
                    />
                  </li>
                ))}
              </ul>
            )}
          </li>
        </ul>
      )}
    </>
  );
};

export default CategorizedArticles;